import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, RefreshCwIcon, SmartphoneIcon, BuildingIcon, ChevronDownIcon, FingerprintIcon, ScanFaceIcon, ShareIcon, CopyIcon, ZapIcon, CheckIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { RaastHome } from './raast/RaastHome';
import { RaastRegister } from './raast/RaastRegister';
import { RaastRelinkDelink } from './raast/RaastRelinkDelink';
import { RaastMerchantPayments } from './raast/RaastMerchantPayments';
import { RaastQrTill } from './raast/RaastQrTill';
import { RaastHistory } from './raast/RaastHistory';
export type RaastView = 'hub' | 'register' | 'relink' | 'merchant' | 'qr_till' | 'history' | 'transfer';
interface RaastScreenProps {
  onClose: () => void;
  onNavigateHome?: () => void;
  initialView?: RaastView;
}
type View = RaastView;
type Step = 'tabs' | 'enter_amount' | 'review' | 'mpin' | 'processing' | 'success';
type TopTab = 'transfer' | 'management';
type TransferMethod = 'raast_id' | 'account' | 'iban';
const PAY_FROM_ACCOUNTS = [{
  id: 'current',
  holder: 'Kashish',
  label: 'Current Account',
  number: '......0123 4567 8910',
  balance: 282992
}, {
  id: 'savings',
  holder: 'Kashish',
  label: 'Savings Account',
  number: '......0456 7890 1234',
  balance: 45500
}];
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
let biometricCounter = 0;
export function RaastScreen({
  onClose,
  onNavigateHome,
  initialView = 'hub'
}: RaastScreenProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [view, setView] = useState<View>(initialView);
  const [step, setStep] = useState<Step>('tabs');
  const [direction, setDirection] = useState(1);
  // Tabs State
  const [activeTopTab, setActiveTopTab] = useState<TopTab>('transfer');
  const [transferMethod, setTransferMethod] = useState<TransferMethod>('raast_id');
  // RAAST ID State
  const [raastId, setRaastId] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchedDetails, setFetchedDetails] = useState<{
    name: string;
    mobile: string;
  } | null>(null);
  // Amount State
  const [amount, setAmount] = useState('');
  const [payFromAccount, setPayFromAccount] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showPayFromDropdown, setShowPayFromDropdown] = useState(false);
  // Auth State
  const [biometricPhase, setBiometricPhase] = useState<'scanning' | 'verified'>('scanning');
  const [scanType, setScanType] = useState<'face' | 'thumb'>('face');
  // RAAST ID Management state
  const [isRaastIdEnabled, setIsRaastIdEnabled] = useState(true);
  const [showToggleConfirm, setShowToggleConfirm] = useState(false);
  const [showManageSheet, setShowManageSheet] = useState(false);
  const [linkedAccountId, setLinkedAccountId] = useState<string>(PAY_FROM_ACCOUNTS[0].id);
  const [pendingAccountId, setPendingAccountId] = useState<string | null>(null);
  const [showLinkSuccess, setShowLinkSuccess] = useState<{
    accountLabel: string;
  } | null>(null);
  const linkedAccount = PAY_FROM_ACCOUNTS.find(a => a.id === linkedAccountId) || PAY_FROM_ACCOUNTS[0];
  const handleBackToHub = () => setView('hub');
  const handleGoHome = () => {
    if (onNavigateHome) onNavigateHome();else onClose();
  };
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  // Theme styles
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-page-warm';
  const cardBg = isPlatinum ? 'bg-[#2A2A2C]' : isDark ? 'bg-[#2D1810]' : 'bg-white';
  const inputBg = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]';
  const textPri = isPlatinum || isDark ? 'text-white' : 'text-[#303030]';
  const textSec = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]';
  const borderClr = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.1)]' : 'border-[#F0E8E3]';
  const handleProceedRaastId = () => {
    setIsFetching(true);
    setTimeout(() => {
      setIsFetching(false);
      setFetchedDetails({
        name: 'Ahmed Khan',
        mobile: `03** ***${raastId.slice(-4)}`
      });
    }, 1500);
  };
  useEffect(() => {
    if (step === 'mpin') {
      setBiometricPhase('scanning');
      const currentScanType = biometricCounter % 2 === 0 ? 'face' : 'thumb';
      setScanType(currentScanType);
      biometricCounter++;
      const timer1 = setTimeout(() => {
        setBiometricPhase('verified');
        const timer2 = setTimeout(() => {
          navigateTo('processing');
        }, 800);
        return () => clearTimeout(timer2);
      }, 2000);
      return () => clearTimeout(timer1);
    }
  }, [step]);
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => {
        navigateTo('success');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [step]);
  const renderTabs = () => {
    const isValidRaastId = raastId.length === 11 && raastId.startsWith('03');
    return <motion.div key="tabs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2519">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2520">
          <button onClick={handleBackToHub} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2521">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2522" />
          </button>
          <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2523">
            RAAST Transfer
          </h1>
          <button onClick={handleGoHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2524">
            <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2525" />
          </button>
        </div>

        {/* Top Tabs */}
        <div className="px-6 mb-6" data-id="element-2526">
          <div className={`flex p-1 rounded-full ${inputBg}`} data-id="element-2527">
            <button onClick={() => {
            setActiveTopTab('transfer');
            setFetchedDetails(null);
          }} className={`flex-1 py-2.5 rounded-full text-[13px] font-bold transition-all ${activeTopTab === 'transfer' ? 'bg-[#F37024] text-white shadow-sm' : textSec}`} data-id="element-2528">
              RAAST Transfer
            </button>
            <button onClick={() => setActiveTopTab('management')} className={`flex-1 py-2.5 rounded-full text-[13px] font-bold transition-all ${activeTopTab === 'management' ? 'bg-[#F37024] text-white shadow-sm' : textSec}`} data-id="element-2529">
              ID Management
            </button>
          </div>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2530">
          {activeTopTab === 'transfer' ? <>
              {/* Segmented Radio Selector */}
              <div className="flex gap-2 mb-6" data-id="element-2531">
                {(['raast_id', 'account', 'iban'] as TransferMethod[]).map(method => <button key={method} onClick={() => {
              setTransferMethod(method);
              setFetchedDetails(null);
            }} className={`flex-1 py-3 rounded-xl text-[12px] font-bold transition-all border ${transferMethod === method ? `border-[#F37024] bg-[#F37024]/10 text-[#F37024]` : `${borderClr} ${cardBg} ${textSec}`}`} data-id="element-2532">
                      {method === 'raast_id' ? 'RAAST ID' : method === 'account' ? 'Account' : 'IBAN'}
                    </button>)}
              </div>

              {transferMethod === 'raast_id' ? <div className="flex-1 flex flex-col" data-id="element-2533">
                  {!fetchedDetails ? <>
                      <p className={`text-[14px] font-bold mb-3 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2534">
                        Enter RAAST ID (Mobile Number)
                      </p>
                      <div className="relative mb-6" data-id="element-2535">
                        <SmartphoneIcon className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textSec}`} data-id="element-2536" />
                        <input type="tel" value={raastId} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 11) setRaastId(val);
                }} placeholder="03XX XXXXXXX" className={`w-full rounded-2xl py-4 pl-12 pr-4 text-[16px] font-medium outline-none tracking-wide border ${isDark ? 'bg-[#3D2518] text-white placeholder:text-[#B8A89E]/70 border-[rgba(243,112,36,0.1)]' : 'bg-white text-[#303030] placeholder:text-[#818285] border-[#F0E8E3]'}`} data-id="element-2537" />
                      </div>

                      <button onClick={handleProceedRaastId} disabled={!isValidRaastId || isFetching} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 flex items-center justify-center gap-2 mt-auto" data-id="element-2538">
                        {isFetching ? <>
                            <RefreshCwIcon className="w-5 h-5 animate-spin" data-id="element-2539" />
                            Fetching...
                          </> : 'Proceed'}
                      </button>
                    </> : <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex-1 flex flex-col" data-id="element-2540">
                      <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-4 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2541">
                        Recipient Details
                      </h3>
                      <div className={`rounded-2xl p-5 border ${cardBg} ${borderClr} mb-6`} data-id="element-2542">
                        <div className="flex items-center gap-4 mb-4" data-id="element-2543">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2544">
                            <ZapIcon className="w-6 h-6 text-[#F37024]" fill="currentColor" data-id="element-2545" />
                          </div>
                          <div data-id="element-2546">
                            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-2547">
                              {fetchedDetails.name}
                            </p>
                            <p className={`text-[13px] ${textSec}`} data-id="element-2548">
                              RAAST ID: {fetchedDetails.mobile}
                            </p>
                          </div>
                        </div>
                        <div className={`flex items-center gap-2 p-3 rounded-xl ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-2549">
                          <CheckCircle2Icon className="w-5 h-5 text-[#22C55E]" data-id="element-2550" />
                          <span className={`text-[12px] font-medium ${textPri}`} data-id="element-2551">
                            RAAST ID Verified
                          </span>
                        </div>
                      </div>

                      <button onClick={() => navigateTo('enter_amount')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-auto" data-id="element-2552">
                        Continue
                      </button>
                    </motion.div>}
                </div> : <div className={`text-center py-16 rounded-2xl border ${cardBg} ${borderClr}`} data-id="element-2553">
                  <BuildingIcon className={`w-12 h-12 mx-auto mb-3 ${textSec}`} data-id="element-2554" />
                  <h3 className={`text-lg font-bold mb-1 ${textPri}`} data-id="element-2555">
                    Coming Soon
                  </h3>
                  <p className={`text-sm ${textSec}`} data-id="element-2556">
                    This transfer method will be available shortly.
                  </p>
                </div>}
            </> : <div className={`rounded-2xl p-6 border ${cardBg} ${borderClr}`} data-id="element-2557">
              <div className="flex items-center justify-between mb-6" data-id="element-2558">
                <div data-id="element-2559">
                  <p className={`text-[12px] font-medium uppercase tracking-wider ${textSec}`} data-id="element-2560">
                    Your RAAST ID
                  </p>
                  <p className={`text-[18px] font-bold mt-1 ${textPri} ${!isRaastIdEnabled ? 'opacity-40' : ''}`} data-id="element-2561">
                    0300 1234567
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-[11px] font-bold ${isRaastIdEnabled ? 'bg-[#22C55E]/10 text-[#22C55E]' : isDark || isPlatinum ? 'bg-white/10 text-[#B8A89E]' : 'bg-black/5 text-[#818285]'}`} data-id="element-2562">
                  {isRaastIdEnabled ? 'Active' : 'Disabled'}
                </div>
              </div>

              {/* Enable/Disable Toggle Row */}
              <div className="flex items-center justify-between mb-5" data-id="element-2563">
                <div className="flex-1 pr-3" data-id="element-2564">
                  <p className={`text-[13px] font-bold ${textPri}`} data-id="element-2565">
                    Enable RAAST ID
                  </p>
                  <p className={`text-[11px] mt-0.5 ${textSec}`} data-id="element-2566">
                    {isRaastIdEnabled ? 'Others can send money to your RAAST ID' : 'Your RAAST ID is currently disabled'}
                  </p>
                </div>
                <button onClick={() => setShowToggleConfirm(true)} role="switch" aria-checked={isRaastIdEnabled} className={`relative shrink-0 w-12 h-7 rounded-full transition-colors duration-300 ${isRaastIdEnabled ? 'bg-[#F37024]' : isDark || isPlatinum ? 'bg-white/15' : 'bg-[#E5DDD7]'}`} data-id="element-2567">
                  <motion.span layout transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md" style={{
                left: isRaastIdEnabled ? 'calc(100% - 26px)' : '2px'
              }} data-id="element-2568" />
                </button>
              </div>

              <div className={`h-px w-full ${borderClr} mb-5`} data-id="element-2569" />
              <div className="flex items-center justify-between" data-id="element-2570">
                <div className={!isRaastIdEnabled ? 'opacity-40' : ''} data-id="element-2571">
                  <p className={`text-[13px] font-medium ${textPri}`} data-id="element-2572">
                    Linked Account
                  </p>
                  <p className={`text-[12px] mt-0.5 ${textSec}`} data-id="element-2573">
                    {linkedAccount.label} ••••
                    {linkedAccount.number.replace(/\D/g, '').slice(-4)}
                  </p>
                </div>
                <button onClick={() => setShowManageSheet(true)} disabled={!isRaastIdEnabled} className="px-4 py-2 rounded-xl text-[13px] font-bold border border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2574">
                  Manage
                </button>
              </div>
            </div>}
        </div>

        {/* Toggle Confirmation Modal */}
        <AnimatePresence data-id="element-2575">
          {showToggleConfirm && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm" onClick={() => setShowToggleConfirm(false)} data-id="element-2576">
              <motion.div initial={{
            scale: 0.9,
            opacity: 0,
            y: 20
          }} animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            opacity: 0,
            y: 10
          }} transition={{
            type: 'spring',
            damping: 22,
            stiffness: 280
          }} onClick={e => e.stopPropagation()} className={`w-full max-w-[340px] rounded-3xl p-6 shadow-2xl ${cardBg} border ${borderClr}`} data-id="element-2577">
                <div className="flex flex-col items-center text-center" data-id="element-2578">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isRaastIdEnabled ? 'bg-[#F37024]/10' : 'bg-[#22C55E]/10'}`} data-id="element-2579">
                    <AlertTriangleIcon className={`w-8 h-8 ${isRaastIdEnabled ? 'text-[#F37024]' : 'text-[#22C55E]'}`} strokeWidth={2} data-id="element-2580" />
                  </div>
                  <h3 className={`text-[18px] font-bold mb-2 ${textPri}`} data-id="element-2581">
                    Are you sure?
                  </h3>
                  <p className={`text-[13px] leading-relaxed mb-6 ${textSec}`} data-id="element-2582">
                    {isRaastIdEnabled ? 'Disabling your RAAST ID means others will no longer be able to send you money using your mobile number.' : 'Enabling your RAAST ID will allow others to send you money instantly using your mobile number.'}
                  </p>
                  <div className="flex gap-3 w-full" data-id="element-2583">
                    <button onClick={() => setShowToggleConfirm(false)} className={`flex-1 py-3 rounded-2xl font-bold text-[14px] border-2 ${isDark || isPlatinum ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2584">
                      No
                    </button>
                    <button onClick={() => {
                  setIsRaastIdEnabled(prev => !prev);
                  setShowToggleConfirm(false);
                }} className="flex-1 py-3 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2585">
                      Yes
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Manage Account Bottom Sheet */}
        <AnimatePresence data-id="element-2586">
          {showManageSheet && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowManageSheet(false)} data-id="element-2587">
              <motion.div initial={{
            y: '100%'
          }} animate={{
            y: 0
          }} exit={{
            y: '100%'
          }} transition={{
            type: 'spring',
            damping: 28,
            stiffness: 280
          }} onClick={e => e.stopPropagation()} className={`w-full max-w-[430px] rounded-t-3xl p-6 pb-8 shadow-2xl ${cardBg}`} data-id="element-2588">
                <div className="flex justify-center mb-4" data-id="element-2589">
                  <div className={`w-10 h-1.5 rounded-full ${isDark || isPlatinum ? 'bg-white/20' : 'bg-[#E5DDD7]'}`} data-id="element-2590" />
                </div>
                <div className="flex items-center justify-between mb-1" data-id="element-2591">
                  <h3 className={`text-[18px] font-bold ${textPri}`} data-id="element-2592">
                    Change Linked Account
                  </h3>
                  <button onClick={() => setShowManageSheet(false)} className={`p-1 -mr-1 ${textSec}`} data-id="element-2593">
                    <XIcon className="w-5 h-5" data-id="element-2594" />
                  </button>
                </div>
                <p className={`text-[13px] mb-5 ${textSec}`} data-id="element-2595">
                  Select an account to link with your RAAST ID
                </p>

                <div className="space-y-3" data-id="element-2596">
                  {PAY_FROM_ACCOUNTS.map(acc => {
                const isLinked = acc.id === linkedAccountId;
                return <button key={acc.id} onClick={() => {
                  if (isLinked) return;
                  setLinkedAccountId(acc.id);
                  setShowManageSheet(false);
                  setShowLinkSuccess({
                    accountLabel: acc.label
                  });
                  setTimeout(() => setShowLinkSuccess(null), 2500);
                }} className={`w-full text-left rounded-2xl p-4 flex items-center gap-4 border-2 transition-all ${isLinked ? 'border-[#F37024] bg-[#F37024]/5' : `${borderClr} ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}`} data-id="element-2597">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isDark || isPlatinum ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2598">
                          <BuildingIcon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-2599" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2600">
                          <div className="flex items-center gap-2" data-id="element-2601">
                            <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2602">
                              {acc.label}
                            </p>
                            {isLinked && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#F37024] text-white" data-id="element-2603">
                                Linked
                              </span>}
                          </div>
                          <p className={`text-[12px] ${textSec}`} data-id="element-2604">
                            {acc.number}
                          </p>
                          <p className={`text-[11px] mt-0.5 ${textSec}`} data-id="element-2605">
                            Balance: PKR {acc.balance.toLocaleString()}
                          </p>
                        </div>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 ${isLinked ? 'bg-[#F37024] border-[#F37024]' : isDark || isPlatinum ? 'border-white/20' : 'border-[#E5DDD7]'}`} data-id="element-2606">
                          {isLinked && <CheckIcon className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-2607" />}
                        </div>
                      </button>;
              })}
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Link Success Popup */}
        <AnimatePresence data-id="element-2608">
          {showLinkSuccess && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/50 backdrop-blur-sm" onClick={() => setShowLinkSuccess(null)} data-id="element-2609">
              <motion.div initial={{
            scale: 0.85,
            opacity: 0,
            y: 20
          }} animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            opacity: 0
          }} transition={{
            type: 'spring',
            damping: 18,
            stiffness: 240
          }} onClick={e => e.stopPropagation()} className={`w-full max-w-[320px] rounded-3xl p-6 shadow-2xl text-center ${cardBg} border ${borderClr}`} data-id="element-2610">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 14,
              stiffness: 220,
              delay: 0.1
            }} className="w-16 h-16 mx-auto bg-[#22C55E] rounded-full flex items-center justify-center mb-4 shadow-lg shadow-[#22C55E]/30" data-id="element-2611">
                  <CheckCircle2Icon className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2612" />
                </motion.div>
                <h3 className={`text-[18px] font-bold mb-2 ${textPri}`} data-id="element-2613">
                  Account Updated
                </h3>
                <p className={`text-[13px] leading-relaxed ${textSec}`} data-id="element-2614">
                  Your RAAST ID is now linked to{' '}
                  <span className={`font-bold ${textPri}`} data-id="element-2615">
                    {showLinkSuccess.accountLabel}
                  </span>{' '}
                  successfully.
                </p>
                <button onClick={() => setShowLinkSuccess(null)} className="mt-5 w-full py-3 rounded-2xl font-bold text-[14px] bg-[#F37024] text-white shadow-md" data-id="element-2616">
                  Done
                </button>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  };
  const renderEnterAmount = () => {
    return <motion.div key="enter_amount" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2617">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2618">
          <button onClick={() => navigateTo('tabs', -1)} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2619">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2620" />
          </button>
          <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2621">
            Enter Amount
          </h1>
        </div>

        <div className="px-6 mt-2 flex-1 flex flex-col pb-8" data-id="element-2622">
          {/* Pay From Account Selector */}
          <div className="mb-8 relative z-20" data-id="element-2623">
            <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-3 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-2624">
              Pay From
            </h3>
            <button onClick={() => setShowPayFromDropdown(!showPayFromDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between border shadow-sm ${isDark ? 'bg-[#2D1810] border-[rgba(243,112,36,0.1)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2625">
              <div className="text-left" data-id="element-2626">
                <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2627">
                  {payFromAccount.label}
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-2628">
                  {payFromAccount.number}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-2629" />
            </button>
            <p className={`text-[12px] mt-2 ml-2 ${textSec}`} data-id="element-2630">
              Available Balance: PKR {payFromAccount.balance.toLocaleString()}
              .00
            </p>

            <AnimatePresence data-id="element-2631">
              {showPayFromDropdown && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className={`absolute top-[80px] left-0 right-0 rounded-2xl shadow-xl overflow-hidden border ${isDark ? 'bg-[#3D2518] border-[rgba(243,112,36,0.2)]' : 'bg-white border-[#F0E8E3]'}`} data-id="element-2632">
                  {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                setPayFromAccount(acc);
                setShowPayFromDropdown(false);
              }} className={`w-full text-left p-4 border-b last:border-0 ${isDark ? 'border-[rgba(243,112,36,0.1)] hover:bg-white/5' : 'border-[#F0E8E3] hover:bg-black/5'}`} data-id="element-2633">
                      <p className={`font-bold ${textPri}`} data-id="element-2634">{acc.label}</p>
                      <p className={`text-sm ${textSec}`} data-id="element-2635">{acc.number}</p>
                    </button>)}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Amount Input */}
          <div className="flex flex-col items-center mb-auto" data-id="element-2636">
            <p className={`text-[13px] font-bold mb-4 uppercase tracking-wider ${isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-2637">
              Currency: PKR
            </p>
            <div className={`flex items-center gap-4 border-b-2 pb-2 min-w-[200px] justify-center ${isDark ? 'border-[rgba(243,112,36,0.3)]' : 'border-[#F0E8E3]'}`} data-id="element-2638">
              <span className={`text-[24px] font-bold ${textSec}`} data-id="element-2639">PKR</span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className={`w-[140px] text-[40px] font-bold bg-transparent outline-none text-center ${textPri} placeholder:opacity-30`} autoFocus data-id="element-2640" />
            </div>

            <div className="flex gap-2 mt-8" data-id="element-2641">
              {[200, 500, 1000, 2000].map(val => <button key={val} onClick={() => setAmount(val.toString())} className={`px-4 py-2 rounded-full text-[13px] font-bold transition-colors ${amount === val.toString() ? 'bg-[#F37024] text-white' : `${inputBg} ${textPri}`}`} data-id="element-2642">
                  Rs. {val}
                </button>)}
            </div>
          </div>

          <button onClick={() => navigateTo('review')} disabled={!amount || parseInt(amount) <= 0} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2643">
            Next
          </button>
        </div>
      </motion.div>;
  };
  const renderReview = () => {
    return <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2644">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2645">
          <button onClick={() => navigateTo('enter_amount', -1)} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2646">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2647" />
          </button>
          <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2648">
            Review Transfer
          </h1>
        </div>

        <div className="px-6 mt-2 flex-1 flex flex-col pb-8" data-id="element-2649">
          <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2650">
            <div className="flex flex-col items-center mb-8 text-center" data-id="element-2651">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2652">
                <ZapIcon className="w-8 h-8 text-[#F37024]" fill="currentColor" data-id="element-2653" />
              </div>
              <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2654">
                Rs. {parseInt(amount || '0').toLocaleString()}
              </p>
              <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2655">
                To {fetchedDetails?.name || 'Recipient'}
              </p>
            </div>

            <div className="space-y-4" data-id="element-2656">
              <div className="flex justify-between" data-id="element-2657">
                <span className={textSec} data-id="element-2658">From Account</span>
                <span className={`font-bold ${textPri}`} data-id="element-2659">
                  {payFromAccount.number.slice(-4)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-2660">
                <span className={textSec} data-id="element-2661">Transfer Method</span>
                <span className={`font-bold ${textPri}`} data-id="element-2662">RAAST</span>
              </div>
              <div className="flex justify-between" data-id="element-2663">
                <span className={textSec} data-id="element-2664">RAAST ID</span>
                <span className={`font-bold ${textPri}`} data-id="element-2665">
                  {fetchedDetails?.mobile || raastId}
                </span>
              </div>
              <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2666" />
              <div className="flex justify-between" data-id="element-2667">
                <span className={textSec} data-id="element-2668">Fee</span>
                <span className={`font-bold ${textPri}`} data-id="element-2669">Rs. 0</span>
              </div>
              <div className="flex justify-between" data-id="element-2670">
                <span className={`font-bold ${textPri}`} data-id="element-2671">Total Amount</span>
                <span className={`font-bold text-[#F37024]`} data-id="element-2672">
                  Rs. {parseInt(amount || '0').toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <button onClick={() => navigateTo('mpin')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2673">
            Confirm & Transfer
          </button>
        </div>
      </motion.div>;
  };
  const renderMpin = () => {
    return <motion.div key="mpin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${bgStyle}`} data-id="element-2674">
        <AnimatePresence mode="wait" data-id="element-2675">
          {biometricPhase === 'scanning' && <motion.div key="scanning" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.8
        }} className="flex flex-col items-center gap-6" data-id="element-2676">
              <motion.div animate={{
            scale: [1, 1.2, 1],
            opacity: [0.6, 1, 0.6]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-24 h-24 rounded-full flex items-center justify-center" style={{
            background: 'rgba(243, 112, 36, 0.12)',
            border: '2px solid rgba(243, 112, 36, 0.3)'
          }} data-id="element-2677">
                {scanType === 'thumb' ? <FingerprintIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-2678" /> : <ScanFaceIcon className="w-12 h-12 text-[#F37024]" strokeWidth={1.5} data-id="element-2679" />}
              </motion.div>
              <div className="text-center" data-id="element-2680">
                <p className={`text-[18px] font-bold mb-1 ${textPri}`} data-id="element-2681">
                  {scanType === 'thumb' ? 'Scanning Fingerprint' : 'Scanning Face'}
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-2682">
                  {scanType === 'thumb' ? 'Place your finger on the sensor' : 'Look at the camera'}
                </p>
              </div>
            </motion.div>}

          {biometricPhase === 'verified' && <motion.div key="verified" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.8
        }} className="flex flex-col items-center gap-6" data-id="element-2683">
              <motion.div initial={{
            scale: 0.3
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200
          }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center" style={{
            border: '2px solid rgba(34, 197, 94, 0.3)'
          }} data-id="element-2684">
                <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={1.5} data-id="element-2685" />
              </motion.div>
              <div className="text-center" data-id="element-2686">
                <p className={`text-[18px] font-bold mb-1 ${textPri}`} data-id="element-2687">
                  Verified Successfully
                </p>
                <p className={`text-[13px] ${textSec}`} data-id="element-2688">
                  Processing transaction...
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  };
  const renderProcessing = () => {
    return <motion.div key="processing" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className={`w-full min-h-screen flex flex-col items-center justify-center ${bgStyle}`} data-id="element-2689">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'linear'
      }} className="w-16 h-16 border-4 border-[#F37024]/20 border-t-[#F37024] rounded-full mb-6" data-id="element-2690" />
        <h2 className={`text-xl font-bold mb-2 ${textPri}`} data-id="element-2691">
          Processing Transfer
        </h2>
        <p className={`text-sm ${textSec}`} data-id="element-2692">
          Please wait while we process your request...
        </p>
      </motion.div>;
  };
  const renderSuccess = () => {
    return <motion.div key="success" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2693">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12" data-id="element-2694">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 15,
          stiffness: 200,
          delay: 0.1
        }} className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#22C55E]/30" data-id="element-2695">
            <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-2696" />
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className={`text-2xl font-bold mb-2 text-center ${textPri}`} data-id="element-2697">
            Transfer Successful!
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className={`text-[15px] text-center mb-8 ${textSec}`} data-id="element-2698">
            Your transfer of{' '}
            <span className="font-bold text-[#F37024]" data-id="element-2699">
              Rs. {parseInt(amount || '0').toLocaleString()}
            </span>{' '}
            to {fetchedDetails?.name || 'Recipient'} has been completed
            successfully via RAAST.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className={`w-full rounded-[24px] p-6 shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2700">
            <div className="space-y-4" data-id="element-2701">
              <div className="flex justify-between" data-id="element-2702">
                <span className={textSec} data-id="element-2703">Reference ID</span>
                <span className={`font-bold ${textPri}`} data-id="element-2704">
                  RST-{Math.floor(Math.random() * 1000000000)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-2705">
                <span className={textSec} data-id="element-2706">Date & Time</span>
                <span className={`font-bold ${textPri}`} data-id="element-2707">
                  {new Date().toLocaleDateString('en-PK', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                  ,{' '}
                  {new Date().toLocaleTimeString('en-PK', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-2708">
                <span className={textSec} data-id="element-2709">RAAST ID</span>
                <span className={`font-bold ${textPri}`} data-id="element-2710">
                  {fetchedDetails?.mobile || raastId}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="px-6 pb-8 flex gap-3" data-id="element-2711">
          <button className={`flex-1 py-4 rounded-2xl font-bold text-[15px] flex items-center justify-center gap-2 border-2 transition-colors ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#F0E8E3] text-[#303030] hover:bg-black/5'}`} data-id="element-2712">
            <ShareIcon className="w-5 h-5" data-id="element-2713" />
            Share
          </button>
          <button onClick={handleGoHome} className="flex-[2] py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md" data-id="element-2714">
            Done
          </button>
        </motion.div>
      </motion.div>;
  };
  if (view === 'hub') return <RaastHome onNavigate={v => setView(v as View)} onClose={onClose} data-id="element-2715" />;
  if (view === 'register') return <RaastRegister onBack={handleBackToHub} onHome={handleGoHome} data-id="element-2716" />;
  if (view === 'relink') return <RaastRelinkDelink onBack={handleBackToHub} onHome={handleGoHome} data-id="element-2717" />;
  if (view === 'merchant') return <RaastMerchantPayments onBack={handleBackToHub} onHome={handleGoHome} onTransfer={() => setView('transfer')} data-id="element-2718" />;
  if (view === 'qr_till') return <RaastQrTill onBack={handleBackToHub} onHome={handleGoHome} onTransfer={() => setView('transfer')} data-id="element-2719" />;
  if (view === 'history') return <RaastHistory onBack={handleBackToHub} onHome={handleGoHome} data-id="element-2720" />;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2721">
      {step === 'tabs' && renderTabs()}
      {step === 'enter_amount' && renderEnterAmount()}
      {step === 'review' && renderReview()}
      {step === 'mpin' && renderMpin()}
      {step === 'processing' && renderProcessing()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}